import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import chipLogo from './images/Elinnos-chip.png';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Eye, EyeOff, Mail, Lock, User as UserIcon, Sparkles, Cpu, Zap, ShieldCheck, ArrowRight } from 'lucide-react';
import ApiService from '../services/api';
import ErrorHandler from '../utils/errorHandler';
import Logger from '../utils/logger';
import { toast } from '../hooks/use-toast';

// Social provider icons
const GoogleIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" {...props}>
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.3h6.47c-.28 1.5-1.13 2.77-2.4 3.62v3.01h3.86c2.26-2.09 3.56-5.17 3.56-8.66z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3.01c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.27v3.11C3.24 21.3 7.31 24 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.27a7.16 7.16 0 0 1 0-4.54V6.62H1.27a11.98 11.98 0 0 0 0 10.76l4-3.11z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.94 1.19 15.24 0 12 0 7.31 0 3.24 2.7 1.27 6.62l4 3.11C6.22 6.87 8.87 4.75 12 4.75z" />
  </svg>
);

const MicrosoftIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" {...props}>
    <rect x="1" y="1" width="10" height="10" fill="#F25022" />
    <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
    <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
    <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
  </svg>
);

const GitHubIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.44 9.8 8.21 11.39.6.11.82-.26.82-.58 0-.29-.01-1.04-.02-2.04-3.34.72-4.04-1.61-4.04-1.61-.55-1.39-1.34-1.76-1.34-1.76-1.09-.75.08-.73.08-.73 1.2.09 1.84 1.24 1.84 1.24 1.07 1.84 2.81 1.3 3.5.99.11-.77.42-1.3.76-1.6-2.67-.3-5.47-1.34-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.12-.3-.54-1.52.12-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 016.02 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.66.24 2.88.12 3.18.77.84 1.24 1.91 1.24 3.22 0 4.61-2.81 5.62-5.48 5.92.43.37.81 1.1.81 2.22 0 1.61-.01 2.9-.01 3.29 0 .32.22.7.83.58C20.56 21.79 24 17.3 24 12 24 5.37 18.63 0 12 0z" />
  </svg>
);

// Decorative isometric chip illustration for the login left panel
const ChipIllustration = () => (
  <svg viewBox="0 0 340 340" width="100%" height="100%" style={{ overflow: 'visible' }}>
    <defs>
      <linearGradient id="chipTopFace" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#bfdbfe" />
        <stop offset="55%" stopColor="#60a5fa" />
        <stop offset="100%" stopColor="#2563eb" />
      </linearGradient>
      <linearGradient id="chipRightFace" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#1d4ed8" />
        <stop offset="100%" stopColor="#1e3a8a" />
      </linearGradient>
      <linearGradient id="chipFrontFace" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#1e40af" />
        <stop offset="100%" stopColor="#172554" />
      </linearGradient>
      <linearGradient id="dieFace" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#eff6ff" />
        <stop offset="100%" stopColor="#93c5fd" />
      </linearGradient>
      <linearGradient id="pinGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#e2e8f0" />
        <stop offset="100%" stopColor="#94a3b8" />
      </linearGradient>
      <filter id="chipShadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="14" stdDeviation="14" floodColor="#1e3a8a" floodOpacity="0.35" />
      </filter>
    </defs>

    <g filter="url(#chipShadow)">
      {/* Pins - left edge (front face side) */}
      {[0, 1, 2, 3, 4].map((i) => (
        <rect key={`pl-${i}`} x="30" y={100 + i * 24} width="30" height="9" rx="2" fill="url(#pinGrad)" />
      ))}
      {/* Pins - bottom-front edge */}
      {[0, 1, 2, 3, 4].map((i) => (
        <rect key={`pf-${i}`} x={80 + i * 22} y="248" width="9" height="26" rx="2" fill="url(#pinGrad)" />
      ))}
      {/* Pins - bottom-right edge */}
      {[0, 1, 2, 3].map((i) => (
        <rect key={`pr-${i}`} x={200 + i * 22} y="205" width="9" height="26" rx="2" transform={`skewX(-20)`} fill="url(#pinGrad)" opacity="0.92" />
      ))}
      {/* Pins - top-right edge */}
      {[0, 1, 2, 3].map((i) => (
        <rect key={`pt-${i}`} x={245 + i * 4} y={70 - i * 10} width="26" height="9" rx="2" transform={`rotate(${-28} ${245 + i * 4} ${70 - i * 10})`} fill="url(#pinGrad)" opacity="0.85" />
      ))}

      {/* Chip body - top face */}
      <polygon points="60,100 220,55 280,95 120,145" fill="url(#chipTopFace)" />
      {/* Chip body - right face */}
      <polygon points="280,95 280,185 120,230 120,145" fill="url(#chipRightFace)" />
      {/* Chip body - front face */}
      <polygon points="60,100 120,145 120,230 60,180" fill="url(#chipFrontFace)" />

      {/* Beveled edge highlight along top-front seam */}
      <polygon points="60,100 120,145 118,150 58,105" fill="#dbeafe" opacity="0.5" />

      {/* Die (inner IC square) recessed into top face */}
      <polygon points="105,108 205,80 232,100 132,130" fill="url(#dieFace)" opacity="0.9" />
      <polygon points="105,108 205,80 208,84 108,112" fill="#ffffff" opacity="0.5" />

      {/* Corner pin-1 indicator dot */}
      <circle cx="118" cy="122" r="4" fill="#1e3a8a" opacity="0.6" />

      {/* Glossy diagonal highlight sweep across top face */}
      <polygon points="60,100 220,55 232,63 72,108" fill="#ffffff" opacity="0.3" />
    </g>
  </svg>
);

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    // Check for redirect parameter in URL
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    Logger.debug('Checking URL for redirect param', { redirect });
    if (redirect) {
      if (redirect === 'landing') {
        Logger.debug('Found redirect=landing, will redirect to / after login');
        setRedirectAfterLogin('/');
      } else {
        Logger.debug('Found redirect URL, will redirect after login', { redirect });
        setRedirectAfterLogin(redirect);
      }
    }
    
    // Initial auth check
    checkAuthStatus();
  }, []);

  // Handle redirect after user is set
  useEffect(() => {
    if (shouldRedirect && redirectAfterLogin && user) {
      Logger.debug('Performing redirect', { redirectAfterLogin });
      setShouldRedirect(false);
      setTimeout(() => {
        window.location.href = redirectAfterLogin;
      }, 50);
    }
  }, [shouldRedirect, redirectAfterLogin, user]);

  useEffect(() => {
    // Check for session_id in URL fragment
    const handleAuth = async () => {
      const hash = window.location.hash;
      if (hash.includes('session_id=')) {
        setLoading(true);
        const sessionId = hash.split('session_id=')[1].split('&')[0];

        try {
          const result = await ApiService.processSession(sessionId);
          setUser(result.user);
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          Logger.error('Session processing failed', { error });
        } finally {
          setLoading(false);
        }
      }
    };

    handleAuth();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Auth check timeout after 10 seconds')), 10000)
      );
      
      const currentUser = await Promise.race([
        ApiService.getCurrentUser(),
        timeoutPromise
      ]);
      
      // Check if user is anonymous - treat as not authenticated
      const isAnonymous = currentUser && (
        currentUser.email === 'anonymous@chipdesigner.ai' ||
        currentUser.email?.includes('anonymous') ||
        (currentUser.email?.startsWith('user_') && currentUser.email?.endsWith('@chipdesigner.ai')) ||
        currentUser.name === 'Anonymous User' ||
        currentUser.name?.includes('Anonymous')
      );
      
      if (isAnonymous) {
        Logger.debug('Anonymous user detected, treating as not authenticated', { 
          email: currentUser?.email, 
          name: currentUser?.name 
        });
        setUser(null);
      } else if (currentUser) {
        Logger.debug('Auth check successful', { userEmail: currentUser?.email });
        setUser(currentUser);
      } else {
        setUser(null);
      }
    } catch (error) {
      Logger.error('Auth check failed', { error });
      // User not authenticated
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async (credentialResponse) => {
    try {
      setLoading(true);
      const result = await ApiService.googleAuth(credentialResponse.credential);
      
      // Check if we should redirect
      if (redirectAfterLogin) {
        Logger.info('Google login successful, redirecting', { redirectAfterLogin });
        setUser(result.user);
        setTimeout(() => {
          window.location.href = redirectAfterLogin;
        }, 50);
        return;
      }
      
      setUser(result.user);
    } catch (error) {
      const handled = ErrorHandler.handle(error, {
        component: 'AuthWrapper',
        action: 'loginWithGoogle',
        endpoint: '/auth/google',
        metadata: { hasCredential: !!credentialResponse?.credential }
      });
      
      if (process.env.NODE_ENV === 'development') {
        Logger.error('Google login error in AuthWrapper', {
          handled,
          error,
          response: error.response,
          status: error.response?.status
        });
      }
      
      // Re-throw with handled error so it can be caught by handleGoogleLogin in LoginScreen
      const enhancedError = new Error(handled.message);
      enhancedError.code = handled.code;
      enhancedError.category = handled.category;
      enhancedError.handled = handled;
      enhancedError.originalError = error;
      throw enhancedError;
    } finally {
      setLoading(false);
    }
  };

  const login = async () => {
    try {
      setLoading(true);
      // Create a simple session without external auth
      const sessionId = `simple_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const result = await ApiService.processSession(sessionId);
      setUser(result.user);
    } catch (error) {
      Logger.error('Login failed', { error });
    } finally {
      setLoading(false);
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      setLoading(true);
      const result = await ApiService.emailLogin(email, password);
      
      // Check if we should redirect
      if (redirectAfterLogin) {
        Logger.debug('Email login successful! Setting user and triggering redirect', { redirectAfterLogin });
        setUser(result.user);
        setTimeout(() => {
          window.location.href = redirectAfterLogin;
        }, 50);
        return;
      }
      
      setUser(result.user);
      toast({
        variant: 'success',
        title: '✓ Login successful',
        description: 'Welcome back! You have signed in successfully.',
      });
    } catch (error) {
      Logger.error('Email login failed', { error });
      // Don't redirect on error - let handleLogin handle the error display
      // Reset loading state will be handled in finally block
      throw error; // Re-throw so handleLogin can catch it
    } finally {
      setLoading(false);
    }
  };

  const registerWithEmail = async (name, email, password, couponCode) => {
    try {
      setLoading(true);
      const result = await ApiService.emailRegister(name, email, password, couponCode);
      
      // Check if we should redirect after registration
      if (redirectAfterLogin) {
        Logger.debug('Registration successful! Setting user and triggering redirect', { redirectAfterLogin });
        setUser(result.user);
        setTimeout(() => {
          window.location.href = redirectAfterLogin;
        }, 50);
        return;
      }
      
      setUser(result.user);
      toast({
        variant: 'success',
        title: '✓ Registration successful',
        description: 'Welcome! Your account has been created successfully.',
      });
    } catch (error) {
      Logger.error('Registration failed', { error });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await ApiService.logout();
      // Clear the session cookie on frontend as well
      ApiService.clearSessionCookie();
      setUser(null);
    } catch (error) {
      Logger.error('Logout failed', { error });
      // Force logout on frontend even if backend fails
      ApiService.clearSessionCookie();
      setUser(null);
    }
  };

  const value = {
    user,
    login,
    loginWithGoogle,
    loginWithEmail,
    registerWithEmail,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

const LoginScreen = () => {
  const { loginWithEmail, registerWithEmail, loginWithGoogle, loading } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [chipTilt, setChipTilt] = useState({ rx: 5, ry: 0 });
  const chipContainerRef = useRef(null);

  const handleChipMouseMove = (e) => {
    if (!chipContainerRef.current) return;
    const rect = chipContainerRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    setChipTilt({ rx: 5 + dy * 15, ry: dx * 25 });
  };

  const handleChipMouseLeave = () => {
    setChipTilt({ rx: 5, ry: 0 });
  };

  useEffect(() => {
    const savedTheme = document.documentElement.getAttribute('data-theme') || 'dark';
    document.documentElement.setAttribute('data-theme', 'dark');
    document.body.setAttribute('data-theme', 'dark');
    return () => {
      document.documentElement.setAttribute('data-theme', savedTheme);
      document.body.setAttribute('data-theme', savedTheme);
    };
  }, []);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [googleLoginError, setGoogleLoginError] = useState('');

  // Register form state
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerCoupon, setRegisterCoupon] = useState('');
  const [registerError, setRegisterError] = useState('');

  // Forgot password modal state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState('idle'); // idle | sending | sent | error
  const [forgotMessage, setForgotMessage] = useState('');

  const toggleMode = () => {
    Logger.debug('Toggling mode', { from: isSignupMode, to: !isSignupMode });
    setIsSignupMode(!isSignupMode);
    // Clear errors when switching
    setLoginError('');
    setRegisterError('');
    setGoogleLoginError('');
  };

  // Handle Google login — uses Google Identity Services (GIS) ID token flow
  // This returns a JWT ID token that the backend can verify with Google's public keys
  const handleGoogleLogin = async () => {
    setGoogleLoginError('');
    setLoginError('');

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

    if (!clientId) {
      setGoogleLoginError('Google sign-in is not configured. Please use email login.');
      toast({ variant: 'destructive', title: 'Google sign-in unavailable', description: 'Google OAuth is not configured. Please use email login.' });
      return;
    }

    if (!window.google || !window.google.accounts || !window.google.accounts.id) {
      setGoogleLoginError('Google sign-in script not loaded. Please refresh and try again.');
      return;
    }

    try {
      // Initialize Google Identity Services with ID token callback
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          if (response.error) {
            setGoogleLoginError('Google authentication failed. Please try again.');
            return;
          }
          // response.credential is the JWT ID token
          if (!response.credential) {
            setGoogleLoginError('Google authentication failed: no credential received.');
            return;
          }
          try {
            await loginWithGoogle({ credential: response.credential });
          } catch (error) {
            const handled = error?.handled || ErrorHandler.handle(error, {
              component: 'LoginScreen',
              action: 'handleGoogleLogin',
              endpoint: '/auth/google',
            });
            setGoogleLoginError(handled.message || 'Google login failed. Please try again.');
            toast({ variant: 'destructive', title: 'Google sign in failed', description: handled.message || 'Google login failed.' });
          }
        },
        auto_select: false,  // Always show account picker
        cancel_on_tap_outside: true,
      });

      // Trigger the One Tap / popup flow
      window.google.accounts.id.prompt((notification) => {
        // If One Tap is not shown (e.g., user dismissed before), fall back to redirect
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Try to render a hidden button and click it as fallback
          // This opens the standard Google sign-in popup
          const container = document.createElement('div');
          container.style.position = 'fixed';
          container.style.top = '-9999px';
          container.style.left = '-9999px';
          document.body.appendChild(container);
          window.google.accounts.id.renderButton(container, {
            type: 'standard',
            size: 'large',
            width: 200,
          });
          // The button click will trigger the callback
          setTimeout(() => {
            const btn = container.querySelector('div[role="button"]') || container.querySelector('a');
            if (btn) btn.click();
            setTimeout(() => document.body.removeChild(container), 1000);
          }, 100);
        }
      });
    } catch (error) {
      setGoogleLoginError('Google sign-in failed to initialize. Please use email login.');
    }
  };

  // Handle GitHub login — redirect to GitHub OAuth
  const handleGitHubLogin = () => {
    const clientId = process.env.REACT_APP_GITHUB_CLIENT_ID || '';
    if (!clientId) {
      toast({ variant: 'destructive', title: 'GitHub sign-in unavailable', description: 'GitHub OAuth is not configured. Please use email login.' });
      return;
    }
    const redirectUri = encodeURIComponent(window.location.origin + '/auth/github/callback');
    const scope = 'user:email read:user';
    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem('github_oauth_state', state);
    window.location.href = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}`;
  };

  // Handle forgot password — send reset email
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      setForgotStatus('error');
      setForgotMessage('Please enter your email address.');
      return;
    }
    setForgotStatus('sending');
    setForgotMessage('');
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const API_PORT = process.env.REACT_APP_API_PORT || '8006';
      const baseUrl = API_BASE || `${window.location.origin.replace(/:\d+$/, '')}:${API_PORT}`;
      const resp = await fetch(`${baseUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await resp.json();
      setForgotStatus('sent');
      setForgotMessage(data.message || 'If the email exists, a password reset link has been sent.');
    } catch (err) {
      setForgotStatus('error');
      setForgotMessage('Could not connect to the server. Please try again later.');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    setLoginError('');
    setGoogleLoginError(''); // Clear Google login error when trying email login

    if (!loginEmail || !loginPassword) {
      setLoginError('Please fill in all fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(loginEmail)) {
      setLoginError('Please enter a valid email address');
      return;
    }

    try {
      await loginWithEmail(loginEmail, loginPassword);
      // Only redirect if login is successful (no error thrown)
    } catch (error) {
      // IMPORTANT: Prevent any redirects/refreshes on error
      e.preventDefault();
      e.stopPropagation();
      
      // Ensure we don't accidentally set user state on error
      // This prevents any redirect useEffects from triggering
      
      const handled = ErrorHandler.handle(error, {
        component: 'AuthWrapper',
        action: 'handleLogin',
        endpoint: '/auth/email/login',
        metadata: { email: loginEmail }
      });
      
      if (process.env.NODE_ENV === 'development') {
        Logger.error('Login error caught in handleLogin', {
          handled,
          originalError: error,
          response: error.response,
          status: error.response?.status,
          data: error.response?.data
        });
      }
      
      // Use ErrorHandler's user-friendly message, but provide additional context for auth errors
      const errorMessage = handled.message?.toLowerCase() || '';
      const originalMessage = error?.message?.toLowerCase() || '';
      // Check both error.response and error.originalError.response (for enhanced errors)
      const responseData = error?.response?.data || error?.originalError?.response?.data || error?.originalError?.data || {};
      const errorDataMessage = (responseData?.message || responseData?.detail || '').toLowerCase();
      const responseStatus = error?.response?.status || error?.originalError?.response?.status;
      
      // Determine error message based on error code and category
      // Check multiple sources: handled code, handled message, original error message, and error data
      if (handled.code === 'HTTP_401' || 
          errorMessage.includes('invalid') || errorMessage.includes('wrong') || errorMessage.includes('unauthorized') || 
          originalMessage.includes('invalid') || originalMessage.includes('wrong') || originalMessage.includes('unauthorized') ||
          errorDataMessage.includes('invalid') || errorDataMessage.includes('wrong') || errorDataMessage.includes('unauthorized') ||
          responseStatus === 401) {
        setLoginError('Invalid email or password. Please check your credentials and try again.');
        toast({
          variant: 'destructive',
          title: 'Login failed',
          description: 'Invalid email or password. Please check your credentials and try again.',
        });
      } else if (handled.code === 'HTTP_404' || 
                 errorMessage.includes('not found') || errorMessage.includes('user') || 
                 originalMessage.includes('not found') || 
                 errorDataMessage.includes('not found') ||
                 responseStatus === 404) {
        setLoginError('Account not found. Please check your email or sign up for a new account.');
        toast({
          variant: 'destructive',
          title: 'Account not found',
          description: 'Please check your email or sign up for a new account.',
        });
      } else if (handled.category === 'network' || handled.code === 'NETWORK_ERROR' || 
                 originalMessage.includes('network') || originalMessage.includes('fetch') || originalMessage.includes('failed to fetch')) {
        setLoginError('Connection error. Please check your internet connection and try again.');
        toast({
          variant: 'destructive',
          title: 'Connection error',
          description: 'Please check your internet connection and try again.',
        });
      } else if (handled.code === 'HTTP_429' || 
                 errorMessage.includes('rate') || errorMessage.includes('limit') || 
                 originalMessage.includes('rate') ||
                 responseStatus === 429) {
        setLoginError('Too many login attempts. Please wait a few minutes before trying again.');
        toast({
          variant: 'destructive',
          title: 'Too many attempts',
          description: 'Please wait a few minutes before trying again.',
        });
      } else {
        // Fallback to ErrorHandler's message or a generic message
        const finalMessage = handled.message || errorDataMessage || originalMessage || 'Login failed. Please try again or contact support if the problem persists.';
        setLoginError(finalMessage);
        toast({
          variant: 'destructive',
          title: 'Login failed',
          description: finalMessage,
        });
      }
      
      // Ensure loading state is reset (loginWithEmail should handle this, but ensure it here too)
      // The loading state is managed in loginWithEmail's finally block, but we can also ensure it here
      
      // Prevent form submission and page refresh
      return false;
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError('');

    if (!registerName || !registerEmail || !registerPassword) {
      setRegisterError('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registerEmail)) {
      setRegisterError('Please enter a valid email address');
      return;
    }

    // Name validation (basic)
    if (registerName.trim().length < 2) {
      setRegisterError('Please enter a valid name (at least 2 characters)');
      return;
    }

    // Password validation — only minimum length, no complexity requirements
    if (registerPassword.length < 6) {
      setRegisterError('Password must be at least 6 characters long');
      return;
    }

    try {
      await registerWithEmail(registerName, registerEmail, registerPassword, registerCoupon || undefined);
    } catch (error) {
      const handled = ErrorHandler.handle(error, {
        component: 'AuthWrapper',
        action: 'handleRegister',
        endpoint: '/auth/email/register',
        metadata: { email: registerEmail }
      });
      
      if (process.env.NODE_ENV === 'development') {
        Logger.error('Registration error', { handled });
      }
      
      // Use ErrorHandler's user-friendly message, but provide additional context for auth errors
      const errorMessage = handled.message?.toLowerCase() || '';
      const originalMessage = error?.message?.toLowerCase() || '';
      const errorDataDetail = error?.response?.data?.detail?.toLowerCase() || '';
      
      // Check for "user already exists" - backend returns 400 with "already exists" message
      if (errorMessage.includes('already') || errorMessage.includes('exists') || 
          originalMessage.includes('already') || originalMessage.includes('exists') ||
          errorDataDetail.includes('already') || errorDataDetail.includes('exists') ||
          handled.code === 'HTTP_409' || 
          (handled.code === 'HTTP_400' && (errorDataDetail.includes('already') || errorDataDetail.includes('exists')))) {
        setRegisterError('An account with this email already exists. Please try logging in instead.');
        toast({
          variant: 'destructive',
          title: 'User already exists',
          description: 'An account with this email already exists. Please try logging in instead.',
        });
      } else if (handled.category === 'network' || handled.code === 'NETWORK_ERROR') {
        setRegisterError('Connection error. Please check your internet connection and try again.');
        toast({
          variant: 'destructive',
          title: 'Connection error',
          description: 'Please check your internet connection and try again.',
        });
      } else if (handled.code === 'HTTP_400') {
        // Generic 400 error (validation failed)
        const detail = error?.response?.data?.detail || 'Invalid registration data. Please check your inputs.';
        setRegisterError(detail);
        toast({
          variant: 'destructive',
          title: 'Registration failed',
          description: detail,
        });
      } else {
        const finalMessage = handled.message || 'Registration failed. Please try again or contact support if the problem persists.';
        setRegisterError(finalMessage);
        toast({
          variant: 'destructive',
          title: 'Registration failed',
          description: finalMessage,
        });
      }
    }
  };

  const features = [
    {
      icon: Cpu,
      title: 'AI-Powered RTL Generation',
      desc: 'Generate clean, synthesizable RTL with AI assistance.',
      iconBg: 'bg-blue-50 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      icon: Zap,
      title: 'Integrated Simulation',
      desc: 'Simulate, analyze and debug \u2013 all in one place.',
      iconBg: 'bg-teal-50 dark:bg-teal-900/30',
      iconColor: 'text-teal-600 dark:text-teal-400',
    },
    {
      icon: ShieldCheck,
      title: 'Secure & Reliable',
      desc: 'Enterprise-grade security for your IP and data.',
      iconBg: 'bg-purple-50 dark:bg-purple-900/30',
      iconColor: 'text-purple-600 dark:text-purple-400',
    },
  ];

  return (
    <div className="min-h-screen flex relative page-transition" style={{ background: '#102040' }}>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ clipPath: 'polygon(0 0, 100% 0, calc(100% - 45px) 100%, 0 100%)', background: 'linear-gradient(to left, #102040 0%, #0d1830 20%, #0c1a35 40%, #0a1525 60%, #080d1a 80%, #060913 100%)' }}>
        {/* Decorative dot grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(96,165,250,0.8) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        {/* Circuit wave background */}
        <div
          className="absolute inset-0 opacity-[0.35] pointer-events-none"
          style={{
            background: 'url(./assets/chipdesigner_circuit_waves.svg) center center / cover no-repeat',
          }}
        />
        {/* Ambient blue glow */}
        <div className="absolute top-1/4 left-0 w-[400px] h-[400px] rounded-full blur-[120px] pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.12) 0%, transparent 60%)' }} />

        <div className="relative z-10 flex flex-col w-full h-full px-14 py-12">
          {/* Logo + Brand */}
          <a href="/" className="flex items-center gap-3 mb-14 hover:opacity-80 transition-opacity">
            <img src="/cder.png" alt="ChipDesigner.ai" className="w-10 h-10 object-cover rounded-md flex-shrink-0" />
            <span className="text-lg font-bold text-white">ChipDesigner.AI</span>
          </a>

          <div className="flex-1 flex flex-col justify-center max-w-md">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
              <span className="text-xs font-bold text-cyan-300 uppercase tracking-wider">AI-Powered Chip Design</span>
            </div>

            <h1 className="text-4xl font-extrabold mb-6 leading-tight text-white">
              Design Smarter.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-300 to-indigo-400">Verify Faster.</span><br />
              Ship Confidently.
            </h1>
            <p className="text-base text-slate-300/70 mb-10 leading-relaxed">
              ChipDesigner.AI is your intelligent copilot for modern VLSI design and verification workflows.
            </p>

            <div className="space-y-4 mb-10">
              {features.map((f, i) => (
                <div key={i} className="flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-4.5 h-4.5 text-cyan-300" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{f.title}</p>
                    <p className="text-sm text-slate-400">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Lock className="w-3.5 h-3.5 flex-shrink-0" />
              <span>Your data is encrypted and secure with enterprise-grade protection.</span>
            </div>
          </div>
        </div>

      </div>

      {/* 3D Chip — positioned over left panel area, outside clip-path/overflow for full mouse interaction */}
      <div
        ref={chipContainerRef}
        className="chip-3d-container hidden lg:flex absolute top-1/2 -translate-y-1/2 z-20 items-center justify-center pointer-events-auto select-none cursor-pointer"
        style={{ right: 'calc(50% - 30px)' }}
        onMouseMove={handleChipMouseMove}
        onMouseLeave={handleChipMouseLeave}
      >
        <div className="relative w-[30rem] h-[30rem] flex items-center justify-center">
          <div
            className="absolute inset-0 rounded-full blur-2xl pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(96,165,250,0.32) 0%, rgba(59,130,246,0.13) 45%, transparent 70%)',
            }}
          />
          <img
            src="/Chip3d.png"
            alt="3D Chip Illustration"
            className="relative w-full h-full object-contain"
            style={{
              filter: 'drop-shadow(0 0 25px rgba(59,130,246,0.3))',
              transform: `rotateX(${chipTilt.rx}deg) rotateY(${chipTilt.ry}deg)`,
              transformStyle: 'preserve-3d',
              transition: 'transform 0.15s ease-out',
            }}
          />
        </div>
      </div>

      {/* Diagonal seam — invisible merge, no visible glow */}
      {/* Both panels share #0a1020 at the boundary so the clip path is invisible */}

      {/* Diagonal ray glow at panel seam */}
      <div
        className="hidden lg:block absolute top-0 left-1/2 h-full pointer-events-none z-20"
        style={{
          width: '120px',
          background: 'linear-gradient(to right, transparent 0%, rgba(59,130,246,0.08) 35%, rgba(96,165,250,0.12) 50%, rgba(59,130,246,0.08) 65%, transparent 100%)',
          transform: 'translateX(-60px) skewX(-12deg)',
          filter: 'blur(20px)',
        }}
      />
      <div
        className="hidden lg:block absolute top-0 left-1/2 h-full pointer-events-none z-20"
        style={{
          width: '24px',
          background: 'linear-gradient(to right, transparent 0%, rgba(96,165,250,0.15) 40%, rgba(147,197,253,0.25) 50%, rgba(96,165,250,0.15) 60%, transparent 100%)',
          transform: 'translateX(-12px) skewX(-12deg)',
          filter: 'blur(6px)',
        }}
      />
      <div
        className="hidden lg:block absolute top-0 left-1/2 h-full pointer-events-none z-20"
        style={{
          width: '3px',
          background: 'linear-gradient(to bottom, transparent 0%, rgba(96,165,250,0.2) 15%, rgba(147,197,253,0.35) 50%, rgba(96,165,250,0.2) 85%, transparent 100%)',
          transform: 'translateX(-1.5px) skewX(-12deg)',
          filter: 'blur(1.5px)',
        }}
      />

      {/* Right Panel - Forms */}
      <div className="flex-1 p-8 flex flex-col items-center justify-center relative overflow-y-auto" style={{ clipPath: 'polygon(45px 0, 100% 0, 100% 100%, 0 100%)', background: 'linear-gradient(to right, #102040 0%, #0d1830 20%, #0c1a35 40%, #0a1525 60%, #080d1a 80%, #060913 100%)' }}>
          {/* Subtle blue glow bridging from left panel */}
          <div
            className="hidden lg:block absolute -top-32 -left-20 w-[40rem] h-[40rem] rounded-full blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 60%)' }}
          />

          {/* Indigo glow bottom-right for depth and balance */}
          <div
            className="hidden lg:block absolute -bottom-32 -right-20 w-[36rem] h-[36rem] rounded-full blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 60%)' }}
          />

          {/* Center glow behind card for floating effect */}
          <div
            className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[28rem] h-[28rem] rounded-full blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 60%)' }}
          />

          {/* Circuit wave SVG background for tech texture */}
          <div
            className="absolute inset-0 opacity-[0.25] pointer-events-none"
            style={{
              background: 'url(./assets/chipdesigner_circuit_waves.svg) center center / cover no-repeat',
            }}
          />

          {/* Mobile logo (visible when left panel is hidden) */}
          <div className="lg:hidden absolute top-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
            <img src={chipLogo} alt="ChipDesigner.ai" className="h-8 w-8 object-contain" />
            <span className="text-lg font-bold gradient-text">chipdesigner.ai</span>
          </div>

          <div className="w-full max-w-md h-[660px] mx-auto relative z-10">
            {/* Seamless Page Transition Container */}
            <div className="relative w-full h-full">
              {/* Login Form */}
              <div
              className={`absolute inset-0 w-full transition-all duration-500 ease-out will-change-transform
              ${isSignupMode
                ? 'opacity-0 -translate-x-full scale-95 pointer-events-none'
                : 'opacity-100 translate-x-0 scale-100'
              }`}
              style={{ transformOrigin: 'center center' }}
            >
              <div className="w-full h-full bg-slate-900/80 backdrop-blur-md rounded-2xl shadow-xl shadow-black/30 border border-slate-700/50 p-6 flex flex-col justify-center overflow-y-auto">
                <div className="text-center mb-6 flex-shrink-0">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 mb-4 shadow-lg shadow-blue-500/20">
                    <ShieldCheck className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Welcome Back</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Sign in to your ChipDesigner.AI account</p>
                </div>

                <div className="flex-1">
                  <form onSubmit={handleLogin} className="space-y-4">

                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none z-10" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className={`h-11 pl-10 pr-4 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg border !focus:ring-0 !focus:outline-none !focus:border-transparent transition-all duration-200 ${
                          loginError
                            ? 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-500 !focus:ring-red-500'
                            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                        }`}
                        required
                      />
                    </div>
                  </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password" className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Password</Label>
                      <button type="button" onClick={() => { setShowForgotPassword(true); setForgotStatus('idle'); setForgotMessage(''); }} className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors duration-200">
                        Forgot password?
                      </button>
                    </div>
                  <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none z-10" />
                      <Input
                        id="login-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className={`h-11 pl-10 pr-11 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg border !focus:ring-0 !focus:outline-none !focus:border-transparent transition-all duration-200 ${
                          loginError
                            ? 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-500 !focus:ring-red-500'
                            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                        }`}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {loginError && (
                    <div className="bg-red-50 dark:bg-red-900/80 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-200 px-4 py-3 rounded-lg text-sm animate-fade-in-up shadow-lg">
                      <div className="flex items-start space-x-2">
                        <svg className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">{loginError}</span>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Signing in...</span>
                      </div>
                    ) : (
                      'Sign In'
                    )}
                  </Button>

                  {/* Signup Link - Right after Sign In button */}
                  <div className="mt-3 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      New to ChipDesigner.AI?
                      <button
                        type="button"
                        onClick={() => {
                          Logger.debug('Create New Account clicked');
                          toggleMode();
                        }}
                        className="ml-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-semibold transition-colors duration-200"
                      >
                        Create new account
                      </button>
                    </p>
                  </div>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-700"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-slate-900 text-slate-500 font-medium">or continue with</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={handleGoogleLogin}
                      className="flex items-center justify-center h-10 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 transition-colors duration-200"
                      aria-label="Continue with Google"
                    >
                      <GoogleIcon />
                    </button>
                    <button
                      type="button"
                      onClick={() => toast({ title: 'Coming soon', description: 'Microsoft sign-in will be available soon.' })}
                      className="flex items-center justify-center h-10 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 transition-colors duration-200"
                      aria-label="Continue with Microsoft"
                    >
                      <MicrosoftIcon />
                    </button>
                    <button
                      type="button"
                      onClick={handleGitHubLogin}
                      className="flex items-center justify-center h-10 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 transition-colors duration-200 text-slate-200"
                      aria-label="Continue with GitHub"
                    >
                      <GitHubIcon />
                    </button>
                  </div>

                  </form>
                </div>

              </div>
            </div>

            {/* Signup Form */}
            <div
              className={`absolute inset-0 w-full transition-all duration-500 ease-out will-change-transform
              ${isSignupMode
                ? 'opacity-100 translate-x-0 scale-100'
                : 'opacity-0 translate-x-full scale-105 pointer-events-none'
              }`}
              style={{ transformOrigin: 'center center' }}
            >
              <div className="w-full h-full bg-slate-900/80 backdrop-blur-md rounded-2xl shadow-xl shadow-black/30 border border-slate-700/50 p-6 flex flex-col justify-center overflow-y-auto">
                <div className="text-center mb-4 flex-shrink-0">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 mb-3 shadow-lg shadow-blue-500/20">
                    <UserIcon className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Create Account</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Join ChipDesigner.AI and start designing chips</p>
                </div>

                <div className="flex-1">
                  <form onSubmit={handleRegister} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Full Name</Label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none z-10" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="John Doe"
                        value={registerName}
                        onChange={(e) => setRegisterName(e.target.value)}
                        className="h-11 pl-10 pr-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg !focus:ring-0 !focus:outline-none !focus:border-transparent transition-all duration-200"
                        required
                      />
                    </div>
                  </div>

              <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none z-10" />
                    <Input
                        id="register-email"
                      type="email"
                        placeholder="your.email@example.com"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        className="h-11 pl-10 pr-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg !focus:ring-0 !focus:outline-none !focus:border-transparent transition-all duration-200"
                      required
                    />
                  </div>
              </div>

              <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none z-10" />
                  <Input
                        id="register-password"
                        type={showRegisterPassword ? "text" : "password"}
                        placeholder="Create a strong password"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        className="h-11 pl-10 pr-11 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg !focus:ring-0 !focus:outline-none !focus:border-transparent transition-all duration-200"
                    required
                  />
                  <button
                    type="button"
                        onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200"
                  >
                        {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Coupon / Promo Code (optional) */}
              <div className="space-y-2">
                <Label htmlFor="register-coupon" className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Promo Code <span className="text-gray-400 normal-case font-normal">(optional)</span></Label>
                <Input
                  id="register-coupon"
                  type="text"
                  placeholder="Enter promo code if you have one"
                  value={registerCoupon}
                  onChange={(e) => setRegisterCoupon(e.target.value)}
                  className="h-11 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg !focus:ring-0 !focus:outline-none !focus:border-transparent transition-all duration-200"
                />
              </div>

                  {registerError && (
                    <div className="bg-red-50 dark:bg-red-900/80 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-200 px-4 py-3 rounded-lg text-sm animate-fade-in-up shadow-lg">
                      <div className="flex items-start space-x-2">
                        <svg className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">{registerError}</span>
                      </div>
                    </div>
                  )}

              <Button
                type="submit"
                disabled={loading}
                    className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Creating account...</span>
                  </div>
                ) : (
                      'Create Account'
                )}
              </Button>

                  {/* Inline Login Link - Right after Create Account button */}
                  <div className="mt-3 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Already have an account?
                      <button
                        type="button"
                        onClick={() => {
                          Logger.debug('Sign in instead clicked');
                          toggleMode();
                        }}
                        className="ml-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-semibold transition-colors duration-200"
                      >
                        Sign in instead
                      </button>
                    </p>
                  </div>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-700"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-slate-900 text-slate-500 font-medium">or continue with</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={handleGoogleLogin}
                      className="flex items-center justify-center h-10 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 transition-colors duration-200"
                      aria-label="Continue with Google"
                    >
                      <GoogleIcon />
                    </button>
                    <button
                      type="button"
                      onClick={() => toast({ title: 'Coming soon', description: 'Microsoft sign-in will be available soon.' })}
                      className="flex items-center justify-center h-10 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 transition-colors duration-200"
                      aria-label="Continue with Microsoft"
                    >
                      <MicrosoftIcon />
                    </button>
                    <button
                      type="button"
                      onClick={handleGitHubLogin}
                      className="flex items-center justify-center h-10 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 transition-colors duration-200 text-slate-200"
                      aria-label="Continue with GitHub"
                    >
                      <GitHubIcon />
                    </button>
                  </div>

                </form>
              </div>

            </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 pb-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs text-slate-500">
            <span className="whitespace-nowrap">&copy; 2026 Elinnos Systems Pvt. Ltd. All rights reserved.</span>
            <span className="hidden sm:inline">&middot;</span>
            <a href="/privacy" className="hover:text-slate-300 whitespace-nowrap">Privacy Policy</a>
            <span>|</span>
            <a href="/terms" className="hover:text-slate-300 whitespace-nowrap">Terms of Service</a>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setShowForgotPassword(false)}
        >
          <div
            className="w-full max-w-md bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold text-white mb-2">Reset your password</h2>
            <p className="text-slate-400 text-sm mb-6">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            {forgotStatus === 'sent' ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-slate-300 text-sm mb-6">{forgotMessage}</p>
                <button
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Email Address</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                    autoFocus
                    className="w-full h-11 px-4 bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>

                {forgotStatus === 'error' && forgotMessage && (
                  <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
                    {forgotMessage}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={forgotStatus === 'sending'}
                    className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
                  >
                    {forgotStatus === 'sending' ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const LoadingScreen = () => {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        <img
          src={chipLogo}
          alt="ChipDesigner.ai"
          className="w-16 h-16 object-contain mx-auto mb-4"
        />
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="mt-4 text-slate-400">Loading ChipDesigner.AI...</p>
      </div>
    </div>
  );
};

const AuthWrapper = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [shouldShowWorkspace, setShouldShowWorkspace] = useState(!!user);

  useEffect(() => {
    // Check if we came from landing page with redirect param
    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    
    if (redirect === 'landing' && user) {
      Logger.debug('User just logged in, redirecting to landing page instead of showing workspace');
      setShouldShowWorkspace(false);
      setTimeout(() => {
        Logger.debug('Redirecting to landing page');
        window.location.href = '/';
      }, 100);
    } else if (user) {
      // Normal workspace access
      setShouldShowWorkspace(true);
    } else {
      setShouldShowWorkspace(false);
    }
  }, [user, location.search]);

  if (loading) {
    return <LoadingScreen />;
  }

  // Show login screen if not authenticated
  if (!user) {
    return <LoginScreen />;
  }

  // If user just logged in from landing page, show loading while redirecting
  if (!shouldShowWorkspace) {
    Logger.debug('Waiting for redirect to landing page');
    return <LoadingScreen />;
  }

  return children;
};

export default AuthWrapper;
